import { useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { getAbTestV2, trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';

import { useFDFQuoteVariant_buyer$key } from './__generated__/useFDFQuoteVariant_buyer.graphql';
import { useFDFQuoteVariant_item$key } from './__generated__/useFDFQuoteVariant_item.graphql';
import { useFDFQuoteVariant_deliveryOptions$key } from './__generated__/useFDFQuoteVariant_deliveryOptions.graphql';
import { useFDFQuoteVariant_relevantShipping$key } from './__generated__/useFDFQuoteVariant_relevantShipping.graphql';

import { getUserGeoInfo } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { usePrerenderedCheckoutStore } from '../prerender/prerenderedCheckoutStoreReact';

const deliveryOptionsFragment = graphql`
    fragment useFDFQuoteVariant_deliveryOptions on ShipmentQuote @relay(plural: true) {
        serviceMethod {
            serviceType
            isFreight
        }
        isFromDealer
        isFromInternal
    }
`;

const buyerFragment = graphql`
    fragment useFDFQuoteVariant_buyer on User {
        addressBook {
            proAddress {
                country
            }
            personalAddress {
                country
            }
            addressList {
                country
            }
        }
    }
`;

const itemFragment = graphql`
    fragment useFDFQuoteVariant_item on Item {
        address {
            country
        }
    }
`;

const relevantShippingFragment = graphql`
    fragment useFDFQuoteVariant_relevantShipping on RelevantShippingInfo {
        shipmentQuotes {
            serviceMethod {
                serviceType
                isFreight
            }
            isFromDealer
            isFromInternal
        }
    }
`;

export const useFDFQuoteVariant = ({
    buyer: buyerRef,
    item: itemRef,
    deliveryOptions: deliveryOptionsRef,
    relevantShipping: relevantShippingRef,
    isFromCheckout,
}: {
    buyer: useFDFQuoteVariant_buyer$key | null;
    item: useFDFQuoteVariant_item$key | null;
    deliveryOptions: useFDFQuoteVariant_deliveryOptions$key | null;
    relevantShipping: useFDFQuoteVariant_relevantShipping$key | null;
    isFromCheckout?: boolean;
}): void => {
    const { rootState } = usePrerenderedCheckoutStore();
    const shouldTrack = isFromCheckout ? ['NOT_INITIALIZED', 'VISIBLE'].includes(rootState) : true;

    const buyer = useFragment(buyerFragment, buyerRef);
    const item = useFragment(itemFragment, itemRef);
    const deliveryOptions = useFragment(deliveryOptionsFragment, deliveryOptionsRef);
    const relevantShipping = useFragment(relevantShippingFragment, relevantShippingRef);
    const shippingOptions = deliveryOptions || relevantShipping?.shipmentQuotes || [];
    const { addressBook } = buyer || {};
    const { proAddress, personalAddress, addressList } = addressBook || {};
    const shipToAddress = proAddress || personalAddress || addressList?.[0];
    const { countryCode } = getUserGeoInfo() || {};
    const userCountry = countryCode || shipToAddress?.country || '';
    const itemLocation = item?.address?.country;
    const hasParcelQuotes = shippingOptions.some(
        quote => quote?.serviceMethod?.serviceType === 'PARCEL'
    );
    const hasSellerFDFQuote = shippingOptions.some(
        quote => quote?.isFromDealer && quote?.serviceMethod?.isFreight
    );

    const dibsFDFQuote = shippingOptions.find(
        quote =>
            quote?.isFromInternal &&
            quote?.serviceMethod?.isFreight &&
            quote?.serviceMethod?.serviceType === 'AIR_FREIGHT'
    );

    const fdfAbQuoteTest = getAbTestV2('fdf-us-row-test-q42023');
    const isFDFQuoteTestVariant = fdfAbQuoteTest?.variant === 'variant';
    const isFDFQuoteTestControl = fdfAbQuoteTest?.variant === 'control';

    const isFdfQuoteABTestEligible = !!(
        itemLocation === 'US' &&
        userCountry !== 'US' &&
        !hasParcelQuotes &&
        !hasSellerFDFQuote &&
        ((isFDFQuoteTestVariant && dibsFDFQuote) || isFDFQuoteTestControl)
    );

    useEffect(() => {
        if (isFdfQuoteABTestEligible && shouldTrack) {
            trackAbTestV2Variant('fdf-us-row-test-q42023');
        }
    }, [isFdfQuoteABTestEligible, shouldTrack]);
};
